// src/components/lesson/CheckpointCta.tsx
//
// The skill check at the bottom of a lesson (code name: checkpoint; students
// never see that word for it, because "Checkpoint" is the ~1-hour diagnostic):
// ten of our own questions on the
// step's topic, scored into the mastery tier the step gates on. This replaces
// the old five-question "try it yourself" drill — the skill check IS the drill,
// and it is the one that moves the student along the path.
//
// THE GATE. A locked step (a prerequisite not yet passed, and no evidence of
// the student's own on the topic) gets no skill check button: the card names
// the prerequisite(s) and links their lessons instead. The lesson itself stays
// readable — content is free — and topic practice from /practice is never
// capped; what the path refuses to do is send a student to a skill check it
// would then tell them they were not ready for. Anonymous visitors, whose
// progress is unknown, see the plain CTA (the session asks them to sign in).

'use client'

import Link from 'next/link'
import type { ReactNode } from 'react'
import { BadgeCheck, Flag, Loader2, Lock, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MasteryMedal } from '@/components/MasteryMedal'
import ProveItButton from '@/components/lesson/ProveItButton'
import { checkpointHref, findStep, type LearningPathStep } from '@/lib/learn/learning-path'
import { gateThresholdPct, type PathStepProgress } from '@/lib/learn/path-progress'
import { unlocksIfPassed } from '@/lib/learn/placement-check'
import { MASTERY_DISPLAY } from '@/lib/test-engine/sat-domains'

export default function CheckpointCta({
  step,
  progress,
  pathSteps = [],
  loading = false,
  secondary,
  onStart,
  onPathChanged,
}: {
  step: Pick<LearningPathStep, 'topic' | 'checkpoint' | 'gate'>
  /** This step's progress once hydrated; null/undefined for anonymous visitors. */
  progress?: PathStepProgress | null
  /**
   * Every step's progress (signed in only) — powers "Prove I know this" here
   * and on a locked step's prerequisites. Empty: no placement offers.
   */
  pathSteps?: PathStepProgress[]
  /** True while the progress is being fetched, so the card does not flash "not started". */
  loading?: boolean
  /** An extra action rendered beside the skill check button (e.g. Print summary). */
  secondary?: ReactNode
  /** Fired when the student clicks through to the skill check (the funnel's `checkpoint_start`). */
  onStart?: () => void
  /** Fired when a "Prove I know this" dialog scored a check — the path changed; re-fetch it. */
  onPathChanged?: () => void
}) {
  const count = step.checkpoint.count
  const gateLabel = MASTERY_DISPLAY[step.gate].label
  const gatePct = gateThresholdPct(step.gate)

  const signedIn = pathSteps.length > 0
  const proveThis =
    signedIn && progress && progress.state !== 'passed' ? (
      <ProveItButton
        topic={step.topic}
        placement={progress.placement}
        unlocks={unlocksIfPassed(step.topic, pathSteps)}
        checkpointCount={count}
        gatePct={gatePct}
        onScored={onPathChanged}
      />
    ) : null

  if (progress?.state === 'locked') {
    const missing = progress.missing.map((topic) => ({
      topic,
      slug: findStep(topic)?.lessonSlug,
      progress: pathSteps.find((s) => s.topic === topic),
    }))
    return (
      <section
        className="mb-8 rounded-xl border border-border bg-muted/40 p-5"
        data-testid="checkpoint-locked"
      >
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Lock className="h-5 w-5 text-muted-foreground" />
          Skill check locked
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          This step builds on {missing.length === 1 ? 'a step' : 'steps'} you have not passed yet.
          Reach {gateLabel} there first — each is a lesson and a {count}-question skill check — and
          this one opens.
        </p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {missing.map((m) => (
            <li key={m.topic} className="flex flex-wrap items-center gap-2">
              {m.slug ? (
                <Link
                  href={`/learn/${m.slug}`}
                  className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-sm text-brand-ink hover:bg-brand/20"
                >
                  <Flag className="h-3 w-3" />
                  {m.topic}
                </Link>
              ) : (
                <span className="text-sm text-foreground">{m.topic}</span>
              )}
              {m.progress && (
                <span className="text-xs text-muted-foreground">
                  <ProveItButton
                    topic={m.topic}
                    placement={m.progress.placement}
                    unlocks={unlocksIfPassed(m.topic, pathSteps)}
                    checkpointCount={count}
                    gatePct={gatePct}
                    variant="link"
                    label="Know it already? Prove it"
                    onScored={onPathChanged}
                  />
                </span>
              )}
            </li>
          ))}
        </ul>
        {(proveThis || secondary) && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {proveThis}
            {secondary}
          </div>
        )}
      </section>
    )
  }

  const passed = progress?.state === 'passed'
  const attempted = progress?.attempted ?? 0

  let status: ReactNode = null
  if (loading) {
    status = (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading your progress…
      </span>
    )
  } else if (progress && passed && progress.passedBy === 'placement') {
    const p = progress.placement.passed
    status = (
      <span className="inline-flex items-center gap-1.5 text-sm text-brand-ink">
        <BadgeCheck className="h-4 w-4" aria-hidden="true" />
        <span>
          Passed by placement{p ? ` · ${p.correct} of ${p.total} on fresh questions` : ''}. The
          skill check below earns the medal.
        </span>
      </span>
    )
  } else if (progress && passed) {
    status = (
      <span className="inline-flex items-center gap-1.5 text-sm text-brand-ink">
        <MasteryMedal tier={progress.tier} />
        <span>
          Passed · {MASTERY_DISPLAY[progress.tier].label} · {progress.accuracyPct}% over {attempted}{' '}
          questions
        </span>
      </span>
    )
  } else if (progress && attempted > 0) {
    const remaining = Math.max(0, count - attempted)
    status = (
      <span className="text-sm text-brand-ink/80">
        {progress.accuracyPct}% over {attempted} so far
        {remaining > 0
          ? ` — ${remaining} more answer${remaining === 1 ? '' : 's'} to earn a tier.`
          : ` — reach ${gatePct}% to pass.`}
      </span>
    )
  }

  return (
    <section className="mb-8 rounded-xl border border-brand/30 bg-brand/10 p-5 dark:bg-brand/20">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-brand-ink">
        <Flag className="h-5 w-5" />
        Skill check
      </h2>
      <p className="mt-1 text-sm text-brand-ink/80">
        {count} questions on {step.topic}, from our question bank. Score {gatePct}% or better across{' '}
        {count} answers to reach {gateLabel} and pass this step.
      </p>
      {status && <div className="mt-2">{status}</div>}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button asChild size="lg" className="bg-brand text-on-brand hover:bg-brand-hover">
          <Link href={checkpointHref(step.topic, count)} onClick={onStart}>
            {passed ? (
              <>
                <RotateCcw className="mr-2 h-4 w-4" />
                Practice again
              </>
            ) : attempted > 0 ? (
              `Continue skill check · ${count} questions`
            ) : (
              `Start skill check · ${count} questions`
            )}
          </Link>
        </Button>
        {proveThis}
        {secondary}
      </div>
    </section>
  )
}
